import { Command } from 'commander';
import chalk from 'chalk';
import fs from 'fs-extra';
import { logger } from '../lib/logger.js';
import { DOCKER_ENV_REL_PATH, ENV_DP_PREVIOUS_PFX } from '../lib/docker-env.js';
import {
  defaultSecretsDir,
  escrowGuidance,
  logCertificateInfo,
  logDockerProvisionResult,
  previousCertificateComposeLines,
  provisionDockerSecrets,
  resolveProjectName,
  writeEscrowCertificate,
} from '../lib/dataprotection-provision.js';
import { findDockerComposeFiles } from '../lib/dataprotection-diagnostics.js';
import { readProjectSmartStackVersion, supportsEmptyCertificateSlot } from '../lib/smartstack-version.js';

interface DataProtectionOptions {
  docker?: boolean;
  env: string;
  out?: string;
  project?: string;
  force?: boolean;
  rotate?: boolean;
}

const ROTATION_PROCEDURE = [
  'Rotation procedure:',
  '  1. Generate the successor (this command with --rotate).',
  '  2. Point DataProtection:Certificate (Pfx + Password) at the NEW certificate.',
  '  3. Move the PREVIOUS one to DataProtection:AdditionalCertificates:0 (Pfx + Password) — it still decrypts',
  '     every key written before the rotation; never drop it while those keys exist.',
  '  4. Restart the API, check its startup log, then escrow both certificates.',
];

async function runDocker(projectDir: string, projectName: string, options: DataProtectionOptions): Promise<void> {
  const composeFiles = await findDockerComposeFiles(projectDir);
  if (composeFiles.length === 0) {
    throw new Error(`no docker compose file under docker-images/ — run ${chalk.cyan('ss upgrade')} to generate the docker files first.`);
  }

  const result = await provisionDockerSecrets({
    projectDir,
    projectName,
    replaceCertificate: Boolean(options.force || options.rotate),
  });
  logDockerProvisionResult(result);

  if (result.previousParked) {
    const compose = (await Promise.all(composeFiles.map((f) => fs.readFile(f, 'utf-8'))))
      .join('\n')
      .split(/\r?\n/)
      .filter((line) => !/^\s*#/.test(line))
      .join('\n');
    console.log();
    logger.info(`The previous certificate was parked in ${ENV_DP_PREVIOUS_PFX} (never deleted).`);
    if (compose.includes(ENV_DP_PREVIOUS_PFX)) {
      // The template's AdditionalCertificates slot (SmartStack >= 3.70) already maps it.
      logger.success('docker-compose.yml passes it to the backend (AdditionalCertificates slot) — nothing to edit.');
    } else {
      const slotSupported = supportsEmptyCertificateSlot(await readProjectSmartStackVersion(projectDir));
      logger.warning(
        'REQUIRED before the next `ss docker up` — add to the backend `environment:` of docker-images/docker-compose.yml' +
        (slotSupported ? ', once and for good:' : ':'),
      );
      for (const line of previousCertificateComposeLines(slotSupported)) console.log(chalk.cyan(`      ${line}`));
      console.log(chalk.yellow(
        slotSupported
          ? '  Left empty between rotations, that slot is ignored by the platform (SmartStack >= 3.70).'
          : '  Without them the keys encrypted by the previous certificate cannot be read (`ss doctor` reports it).\n' +
            '  Before SmartStack 3.70 an EMPTY entry fails startup: keep them only while the previous certificate is set.',
      ));
    }
  }

  console.log();
  logger.info(`Back up ${DOCKER_ENV_REL_PATH} (certificate + password) in a vault — it is gitignored and lives on this machine only.`);
  if (options.rotate) {
    console.log();
    for (const line of ROTATION_PROCEDURE) console.log(line);
  }
}

async function runEscrow(projectDir: string, projectName: string, options: DataProtectionOptions): Promise<void> {
  if (options.force) {
    logger.warning('--force only applies with --docker: an escrow certificate is never overwritten (use --rotate for a successor).');
  }
  const result = await writeEscrowCertificate({
    projectName,
    environment: options.env,
    outDir: options.out ?? defaultSecretsDir(projectName),
    projectDir,
    rotate: options.rotate,
  });

  logger.success(`DataProtection certificate for "${options.env}" written (values not displayed):`);
  console.log(`    PFX:        ${chalk.cyan(result.pfxPath)}`);
  console.log(`    Password:   ${chalk.cyan(result.passwordPath)}`);
  logCertificateInfo(result.certificate);
  console.log();
  for (const line of escrowGuidance()) console.log(line);
  if (result.previousPfxPath) {
    console.log();
    console.log(`Successor of ${chalk.cyan(result.previousPfxPath)} — keep that one: it moves to AdditionalCertificates.`);
  }
  if (options.rotate) {
    console.log();
    for (const line of ROTATION_PROCEDURE) console.log(line);
  }
}

export const secretsCommand = new Command('secrets')
  .description('Provision the secrets a SmartStack project needs outside a dev machine')
  .addCommand(
    new Command('dataprotection')
      .description('Generate the certificate that encrypts the DataProtection key ring at rest (required outside Development/Local)')
      .option('--docker', `Provision ${DOCKER_ENV_REL_PATH} for the docker stack (certificate + JWT secret); idempotent`)
      .option('--env <name>', 'Environment of a deployment certificate (without --docker)', 'production')
      .option('--out <dir>', 'Folder for the deployment certificate, outside any git work tree (default: ~/.smartstack/secrets/<project>/)')
      .option('--project <name>', 'Project name written into the certificate subject (default: detected)')
      .option('--force', 'With --docker: replace an existing certificate (the previous one is parked, never lost)')
      .option('--rotate', 'Generate a successor certificate and print the rotation procedure')
      .action(async (options: DataProtectionOptions) => {
        logger.header('SmartStack DataProtection certificate');
        const projectDir = process.cwd();
        const projectName = options.project ?? await resolveProjectName(projectDir);
        try {
          if (options.docker) {
            await runDocker(projectDir, projectName, options);
          } else {
            await runEscrow(projectDir, projectName, options);
          }
        } catch (error) {
          logger.error(error instanceof Error ? error.message : String(error));
          process.exitCode = 1;
        }
      }),
  )
  .action(() => {
    secretsCommand.outputHelp();
  });
